<script lang="ts" setup>
/**================================================================*/
/*                         Imports
==================================================================*/
import { computed, inject, ref } from "vue"
import * as yup from "yup"

// Types
import type { Services } from "@services"

// Composables
import { useFetchWrapper } from "@core/composables"
import { watchDebounced } from "@vueuse/core"
import { useI18n } from "vue-i18n"
import { useField } from "vee-validate"
import { useIrveStore } from "@modules/irve/stores/irve.store"

// Autres
import { formatIdSource } from "@core/helpers"

type Props = {
    source: string
}
const props = defineProps<Props>()
defineEmits(["update:source"])

const irveStore = useIrveStore()

const { $createGraphBySourceId } = inject<Services>("services")!

// =============================================================
// Gestion du formuliare
// =============================================================
const radioValue = ref("c4")

const { errorMessage, value: modelSource } = useField(
    "source",
    yup.string().required("La sélection d'un compteur est requise"),
    {
        syncVModel: "source",
    }
)

// =============================================================
// Gestion de l'affichage du graphique
// =============================================================
const isVisible = ref(false)
const graph = ref(null)

/**================================================================*/
/*                         Lang
==================================================================*/
const { t } = useI18n({
    messages: {
        fr: {
            info_message:
                "Renseignez les informations de raccordement électrique de l’installation.",
            empty: {
                title: "Simuler sur un nouveau compteur",
                tooltip:
                    "Fonctionnalité bientôt disponible, merci de votre compréhension.",
            },
            c4: {
                title: "Simuler sur un compteur C4 ou C2 du bâtiment",
                tooltip:
                    "Liste des compteurs C4 ou C2 présents sur le bâtiment",
            },
            c5: {
                title: "Simuler sur un compteur C5 du bâtiment - Bientôt disponible !",
                tooltip:
                    "Fonctionnalité bientôt disponible, merci de votre compréhension.",
            },
            add_pv: {
                title: "Ajouter une installation photovoltaïque - Bientôt disponible !",
                tooltip:
                    "Fonctionnalité bientôt disponible, merci de votre compréhension.",
            },
            select_source: {
                label: "Sélectionner un compteur",
                empty: "Aucun compteur disponible",
            },
        },
    },
})

const getButtonConfigList = computed(() => [
    // Bouton simuler sur un compteur vide
    {
        component: "BaseRadioButton",
        props: {
            size: "small",
            modelValue: radioValue,
            value: "empty",
            disabled: true,
        },
        label: t("empty.title"),
        tooltip: t("empty.tooltip"),
    },
    {
        component: "BaseRadioButton",
        props: {
            size: "small",
            modelValue: radioValue,
            value: "c4",
            disabled: false,
        },
        label: t("c4.title"),
        tooltip: t("c4.tooltip"),
    },
    {
        component: "BaseRadioButton",
        props: {
            size: "small",
            modelValue: radioValue,
            value: "c5",
            disabled: true,
        },
        label: t("c5.title"),
        tooltip: t("c5.tooltip"),
    },

    {
        component: "BaseCheckBox",
        props: {
            size: "small",
            value: false,
            disabled: true,
            inputId: "add_pv",
        },
        label: t("add_pv.title"),
        tooltip: t("add_pv.tooltip"),
    },
])

/**
 * Récupère la liste des compteurs de segment C4, C2 du bâtiment
 */
const getSourceListOptions = computed(() => {
    return irveStore.getListPdlForResidualPowerAnalysis.map((source) => ({
        name: `N° ${formatIdSource(source.idsource, "pdl")} ${source.name ? `(${source.name})` : ""}`,
        value: source._id_source,
    }))
})
const fetchGraph = useFetchWrapper(async (_, controller) => {
    if (!modelSource.value) return

    const date_from = new Date(
        new Date().setFullYear(new Date().getFullYear() - 1)
    )
        .toISOString()
        .split("T")[0]

    const date_to = new Date().toISOString().split("T")[0]

    const datasets = [
        {
            render_mode: "source",
            type: "energie_elec_tout.sum",
            name: "Consommation",
            graph_type: "bar",
            unit: "kWh",
            icon: "pi pi-lightbulb",
            icon_color: "#EEC137",
        },
    ]

    const _id_source = modelSource.value as string

    const { data } = await $createGraphBySourceId(
        _id_source,
        date_from,
        date_to,
        "daily",
        "Consommation",
        datasets,
        controller
    )

    graph.value = JSON.parse(data)
})

const isDisplayingGraph = computed(() => {
    return (!fetchGraph.isFetching.value &&
        !!graph.value &&
        isVisible.value &&
        !!props.source) as boolean
})

const isGraphSkeletonDisplayed = computed(
    () => isVisible.value && fetchGraph.isFetching.value
)

watchDebounced(
    () => props.source,
    () => {
        if (fetchGraph.isFetching) fetchGraph.cancel()

        fetchGraph.fetch()
    },
    {
        maxWait: 500,
    }
)

function onClickToggleVisible() {
    isVisible.value = !isVisible.value

    if (fetchGraph.isFetching) fetchGraph.cancel()

    fetchGraph.fetch()
}
</script>

<template>
    <div class="panel-installation flex flex-col gap-6">
        <BaseMessage class="panel-installation__errors" severity="info">
            {{ t("info_message") }}</BaseMessage
        >
        <!-- Contenue du panel -->
        <div
            v-for="(config, index) in getButtonConfigList"
            :key="index"
            class="flex flex-col gap-6"
        >
            <div class="flex items-center gap-2">
                <component :is="config.component" v-bind="config.props" />
                <label
                    :class="
                        config.props.disabled
                            ? 'text-slate-400'
                            : 'text-slate-600'
                    "
                    >{{ config.label }}</label
                >
                <BaseInputIcon
                    v-if="config.tooltip"
                    :class="
                        config.props.disabled
                            ? 'pi pi-info-circle text-slate-400'
                            : 'pi pi-info-circle text-slate-600'
                    "
                    v-tooltip="config.tooltip"
                />
            </div>
            <!-- Si un compteur C4 est selectionner : séléctionner un numéro de compteur -->
            <div
                v-if="radioValue === 'c4' && config.props.value === 'c4'"
                class="flex flex-col gap-6"
            >
                <div
                    class="flex w-full flex-nowrap items-center gap-4 [&_.base-input-wrapper]:w-full"
                >
                    <BaseInputWrapper
                        :error="errorMessage"
                        :label="t('select_source.label')"
                        input-id="source"
                    >
                        <BaseSelect
                            v-model="modelSource"
                            :options="getSourceListOptions"
                            option-label="name"
                            option-value="value"
                            :invalid="!!errorMessage"
                            input-id="source"
                            :empty-message="t('select_source.empty')"
                        />
                        <template #suffix>
                            <BaseInputGroupAddon
                                ><BaseButton
                                    @click="onClickToggleVisible"
                                    :icon="
                                        !isVisible
                                            ? 'pi pi-eye-slash text-cyan-400'
                                            : 'pi pi-eye text-cyan-400'
                                    "
                                    text
                                />
                            </BaseInputGroupAddon>
                        </template>
                    </BaseInputWrapper>
                </div>

                <!-- Si l'icon est cliqué : afficher le graphique associé au compteur
                (1 an de p_active et p_apparente en raw) -->
                <PlotlyChart v-if="isDisplayingGraph" :graph="graph" />
                <BaseChartSkeleton
                    class="pt-6"
                    v-if="isGraphSkeletonDisplayed"
                ></BaseChartSkeleton>
            </div>
        </div>
        <slot name="error"></slot>
    </div>
</template>
